/**
 * CohortVisitorView — Cohort detail page for authenticated users with no role.
 *
 * Tabs: Overview → Activities → Participants → Mentors → Metrics
 *
 * canEnroll and canRequestMentor are computed server-side and available
 * on the cohort object. No frontend eligibility calculation needed.
 */
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Tabs from '@cloudscape-design/components/tabs';
import { Cohort } from '../../api/cohorts';
import { ProgramAdmin } from '../../api/programs';
import { useHashTab } from '../../hooks/useHashTab';
import { useAuthStore } from '../../stores/authStore';
import { useEnrollUser, useRequestMentor, useMentors, useCohortParticipants } from '../../hooks/queries';
import { useNotifications } from '../../components/NotificationFlashbar';
import { useQuery } from '@tanstack/react-query';
import { activitiesApi } from '../../api/activities';
import { queryKeys } from '../../lib/queryClient';
import { CohortOverviewTab } from '../cohort-tabs/CohortOverviewTab';
import { ActivitiesTab } from '../cohort-tabs/ActivitiesTab';
import { ParticipantsTab } from '../cohort-tabs/ParticipantsTab';
import { MentorsTab } from '../cohort-tabs/MentorsTab';
import { ProgressTab } from '../cohort-tabs/MetricsTab';

interface CohortVisitorViewProps {
  cohort: Cohort;
  programAdmins: ProgramAdmin[];
  programWikiUrl?: string;
  programName?: string;
  programOwnerId?: string;
  programOwnerName?: string;
}

export function CohortVisitorView({ cohort, programAdmins, programWikiUrl, programName, programOwnerId, programOwnerName }: CohortVisitorViewProps) {
  const [activeTabId, setActiveTabId] = useHashTab('overview');
  const user = useAuthStore((state) => state.user);
  const { showSuccess } = useNotifications();
  const enrollMutation = useEnrollUser();
  const requestMentorMutation = useRequestMentor();
  const { data: mentorsData } = useMentors(cohort.mentorsEnabled ? cohort.cohortId : undefined);
  const { data: participantsData } = useCohortParticipants(cohort.cohortId);
  const participantCount = participantsData?.participantCount;

  const { data: activities } = useQuery({
    queryKey: queryKeys.activities.list(cohort.cohortId),
    queryFn: () => activitiesApi.list(cohort.cohortId),
  });
  const activityCount = activities?.length ?? 0;

  const handleEnroll = async () => {
    if (!user) return;
    await enrollMutation.mutateAsync({ cohortId: cohort.cohortId, userId: user.userId, programId: cohort.programId });
    showSuccess('Enrolled successfully', `Successfully enrolled in ${cohort.cohortName}! Welcome to the cohort.`);
  };

  const handleRequestMentor = async (notes: string) => {
    if (!user) return;
    await requestMentorMutation.mutateAsync({ cohortId: cohort.cohortId, userId: user.userId, notes });
    showSuccess('Mentor request submitted', 'Program admins will review your request.');
  };

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <CohortOverviewTab
          cohort={cohort}
          programAdmins={programAdmins}
          programWikiUrl={programWikiUrl}
          programOwnerId={programOwnerId}
          programOwnerName={programOwnerName}
          onEnroll={cohort.canEnroll ? handleEnroll : undefined}
          onRequestMentor={cohort.canRequestMentor ? handleRequestMentor : undefined}
        />
      ),
    },
    {
      id: 'activities',
      label: `Activities (${activityCount})`,
      content: (
        <ActivitiesTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentorsEnabled={cohort.mentorsEnabled}
          cohortStartDate={cohort.startDate}
          cohortEndDate={cohort.endDate}
        />
      ),
    },
    {
      id: 'participants',
      label: `Participants${participantCount !== undefined ? ` (${participantCount}${cohort.capacity ? `/${cohort.capacity}` : ''})` : ''}`,
      content: (
        <ParticipantsTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentorsEnabled={cohort.mentorsEnabled}
        />
      ),
    },
    ...(cohort.mentorsEnabled ? [{
      id: 'mentors',
      label: `Mentors (${mentorsData?.length || 0})`,
      content: (
        <MentorsTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentors={mentorsData || []}
          mentorRequests={[]}
          refetchMentors={() => {}}
          refetchMentorRequests={() => {}}
        />
      ),
    }] : []),
    {
      id: 'metrics',
      label: 'Metrics',
      content: (
        <ProgressTab
          cohortId={cohort.cohortId}
          mentorsEnabled={cohort.mentorsEnabled}
          showActivityDetails={false}
          onNavigateToTab={setActiveTabId}
        />
      ),
    },
  ];

  return (
    <SpaceBetween size="m">
      <SpaceBetween size="xxs">
        <Box variant="h1">{programName ?? cohort.cohortName}</Box>
        {programName && <Box variant="h2" color="text-body-secondary">{cohort.cohortName}</Box>}
      </SpaceBetween>
      <Tabs activeTabId={activeTabId} onChange={({ detail }) => setActiveTabId(detail.activeTabId)} tabs={tabs} />
    </SpaceBetween>
  );
}
